import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { filterMentionSuggestions, mentionQueryAtCursor, } from '../lib/updateMentions';
export function UpdateMentionTextarea({ id, value, people, disabled, placeholder, onChange, }) {
    const textareaRef = useRef(null);
    const [cursor, setCursor] = useState(0);
    const [highlight, setHighlight] = useState(0);
    const [dismissedStart, setDismissedStart] = useState(null);
    const [menuBox, setMenuBox] = useState(null);
    const mention = mentionQueryAtCursor(value, cursor);
    const suggestions = useMemo(() => (mention ? filterMentionSuggestions(people, mention.query) : []), [mention, people]);
    const open = Boolean(mention &&
        mention.start !== dismissedStart &&
        suggestions.length > 0 &&
        !disabled);
    useLayoutEffect(() => {
        if (!open) {
            setMenuBox(null);
            return;
        }
        const el = textareaRef.current;
        if (!el)
            return;
        const place = () => {
            const rect = el.getBoundingClientRect();
            setMenuBox({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, value, cursor, suggestions.length]);
    function applySuggestion(suggestion) {
        if (!mention)
            return;
        const insert = `@${suggestion.handle} `;
        const next = `${value.slice(0, mention.start)}${insert}${value.slice(cursor)}`;
        const nextCursor = mention.start + insert.length;
        onChange(next);
        setCursor(nextCursor);
        requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (!el)
                return;
            el.focus();
            el.setSelectionRange(nextCursor, nextCursor);
        });
    }
    return (_jsxs("div", { className: "update-mention-field", children: [_jsx("textarea", { id: id, ref: textareaRef, className: "detail-update-inspire-textarea", rows: 5, value: value, placeholder: placeholder, disabled: disabled, autoFocus: true, onChange: (event) => {
                    onChange(event.target.value);
                    setCursor(event.target.selectionStart);
                    setHighlight(0);
                    setDismissedStart(null);
                }, onSelect: (event) => {
                    setCursor(event.currentTarget.selectionStart);
                }, onKeyDown: (event) => {
                    if (!open)
                        return;
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setHighlight((index) => (index + 1) % suggestions.length);
                    }
                    else if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        setHighlight((index) => (index - 1 + suggestions.length) % suggestions.length);
                    }
                    else if (event.key === 'Enter' || event.key === 'Tab') {
                        event.preventDefault();
                        const suggestion = suggestions[highlight] ?? suggestions[0];
                        if (suggestion)
                            applySuggestion(suggestion);
                    }
                    else if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        setDismissedStart(mention?.start ?? null);
                    }
                } }), open &&
                menuBox &&
                createPortal(_jsx("ul", { className: "update-mention-menu", role: "listbox", "aria-label": "Mention tags and people", style: { top: menuBox.top, left: menuBox.left, width: menuBox.width }, children: suggestions.map((suggestion, index) => (_jsx("li", { children: _jsxs("button", { type: "button", role: "option", "aria-selected": index === highlight, className: index === highlight
                                ? 'update-mention-option is-active'
                                : 'update-mention-option', onMouseDown: (event) => {
                                event.preventDefault();
                                applySuggestion(suggestion);
                            }, children: [_jsx("span", { className: "update-mention-option-name", children: suggestion.label }), _jsx("span", { className: "update-mention-option-handle", children: suggestion.kind === 'tag' ? 'Tag' : `@${suggestion.handle}` })] }) }, `${suggestion.kind}:${suggestion.handle.toLowerCase()}`))) }), document.body)] }));
}
